import {styled} from '@linaria/react';
import React, {forwardRef, PropsWithChildren} from 'react';
import {COLORS} from '../../../constants/colors';
import {isColumnsBreakpoint} from '../../atoms/Image/helpers';
import {Image, Props as ImageProps} from '../../atoms/Image/Image';
import {
  Breakpoint,
  Breakpoints,
  ColumnsBreakpoint,
  Ratio,
  StaticBreakpoint,
} from '../../atoms/Image/types';

export const DEFAULT_PRODUCT_IMAGE_COLUMNS = 4;

export type ProductImageProps = Omit<
  ImageProps,
  'breakpoints' | 'ratio' | 'widths'
> & {
  /*
   * Provide a hint what number of columns this ProductImage will displayed for selecting proper breakpoints for image sizes

   * @deprecated As columns does not allow dynamic values that can change across breakpoints. Use breakpoints instead.
   */
  columns?: number;
  breakpoints?: Breakpoints;
  dataTestId?: string;
  padded?: boolean;
  increasePadding?: boolean;
  className?: string;
  as?: 'button';
  src: string;
  proxyImageSrc?: string;
  widths?: ImageProps['widths'];
  ratio?: Ratio;
  SoldOutComponent?: React.ReactNode;
};

const generateLowQualityBreakpointsForColumn = (
  columns: number,
): Array<ColumnsBreakpoint> => {
  return [
    {breakpoint: '(min-resolution: 4dppx)', columns: 4 * columns},
    {breakpoint: '(min-resolution: 3dppx)', columns: 3 * columns},
    {breakpoint: '(min-resolution: 2dppx)', columns: 2 * columns},
    {breakpoint: '(min-resolution: 1.5dppx)', columns: 1.5 * columns},
    {columns},
  ];
};

const generateLowQualityBreakpointsForWidth = (
  width: string,
): Array<StaticBreakpoint> => {
  return [
    {breakpoint: '(min-resolution: 4dppx)', width: `calc(${width} / 4)`},
    {breakpoint: '(min-resolution: 3dppx)', width: `calc(${width} / 3)`},
    {breakpoint: '(min-resolution: 2dppx)', width: `calc(${width} / 2)`},
    {breakpoint: '(min-resolution: 1.5dppx)', width: `calc(${width} / 1.5)`},
    {width},
  ];
};

const transformLowQualityBreakpointByBreakpoints = (
  breakpoints: Breakpoints,
) => {
  return breakpoints
    .map((breakpoint: Breakpoint) => {
      const lowQualityBreakpoints = isColumnsBreakpoint(breakpoint)
        ? generateLowQualityBreakpointsForColumn(breakpoint.columns)
        : generateLowQualityBreakpointsForWidth(breakpoint.width);
      if (!breakpoint.breakpoint) {
        return lowQualityBreakpoints;
      }

      return lowQualityBreakpoints.map(lowQualityBreakpoint => {
        const combinedBreakpoint = lowQualityBreakpoint.breakpoint
          ? `${breakpoint.breakpoint} and ${lowQualityBreakpoint.breakpoint}`
          : breakpoint.breakpoint;
        if (isColumnsBreakpoint(lowQualityBreakpoint)) {
          return {
            breakpoint: combinedBreakpoint,
            columns: lowQualityBreakpoint.columns,
          } as ColumnsBreakpoint;
        }
        return {
          breakpoint: combinedBreakpoint,
          width: lowQualityBreakpoint.width,
        } as StaticBreakpoint;
      });
    })
    .flat();
};

export const ProductImage = forwardRef<
  HTMLDivElement | HTMLButtonElement,
  PropsWithChildren<ProductImageProps>
>(function ProductImage(
  {
    columns,
    dataTestId,
    src,
    breakpoints,
    proxyImageSrc,
    importance,
    loading,
    alt,
    onLoad,
    children,
    as,
    widths = DEFAULT_WIDTHS,
    ratio = '3:4',
    padded,
    increasePadding,
    SoldOutComponent,
    ...props
  },
  ref,
) {
  // prevent recalculating the object every time which might trigger more re-renderings
  const fallbackBreakpoints =
    breakpoints ||
    ([{columns: columns || DEFAULT_PRODUCT_IMAGE_COLUMNS}] as Breakpoints);
  const ratioParts = ratio.split(':');

  return (
    <StyledProductImage
      {...props}
      as={as as any}
      ref={ref as any}
      data-testid={dataTestId || 'productImage'}
      ratio={Number(ratioParts[1]) / Number(ratioParts[0])}
      data-padded={padded}
      data-increase-padding={increasePadding}
    >
      {SoldOutComponent}
      <Image
        alt={alt}
        applyBorderRadius
        breakpoints={fallbackBreakpoints}
        data-testid={dataTestId ? `${dataTestId}View` : 'productImageView'}
        importance={importance}
        initialBreakpoints={
          importance === 'high'
            ? transformLowQualityBreakpointByBreakpoints(fallbackBreakpoints)
            : undefined
        }
        loading={loading || 'lazy'}
        onLoad={onLoad}
        ratio={ratio}
        src={src}
        proxyImageSrc={proxyImageSrc}
        widths={widths}
      />
      {children}
    </StyledProductImage>
  );
});

const DEFAULT_WIDTHS = [
  120, // optimized for thumb images, most images in this size are 1-2 KB – smaller does not make much sense
  220, // optimized for category mobile (low quality)
  360, // optimized for desktop ADP on small screens (low quality)
  400, // optimized for mobile ADP low (low quality)
  600, // optimized for desktop ADP on small screens (high quality)
  800, // optimized for mobile ADP low (high quality)
  960, // max size for zoom, good compromise of size and quality
];

export const StyledProductImage = styled.div<{
  height?: string | number;
  width?: string | number;
  ratio: number;
}>`
  position: relative;
  width: ${props => (props.width ? `${+props.width}px` : '100%')};
  border-radius: 4px;
  background-color: ${COLORS.ProductImageBackground};
  height: ${props => (props.height ? +props.height : 0)}px;
  padding-top: calc(${props => props.ratio ?? 4 / 3} * 100%);
  overflow: hidden;
  cursor: ${props => (props.onClick ? 'pointer' : 'unset')};

  & > img {
    position: absolute;
    inset: 0;
    max-height: 100%;
    max-width: 100%;
    height: auto;
    margin: auto;
    color: transparent; // hide alt text on Firefox

    @supports (object-fit: cover) {
      object-fit: cover;
      object-position: top; // top align model images
      height: 100%;
      width: 100%;
    }
  }

  &[data-padded='true'] {
    > img {
      inset: 5%;
      max-height: 90%;
      max-width: 90%;

      @supports (object-fit: cover) {
        object-fit: contain;
        object-position: center; // center align bust images
      }
    }

    &[data-increase-padding='true'] {
      > img {
        inset: 0;
        padding: 10% 10% 18% 10%;
        max-height: 100%;
        max-width: 100%;
      }
    }
  }
`;
